/**
 * Feature Detection ===========================================================
 */
// JavaScript ------------------------------------------------------------------
document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

// Touch -----------------------------------------------------------------------
window.isTouchDevice = function() {
  return window.matchMedia("(pointer: coarse)").matches;
}


/**
 * Global P4 Object ============================================================
 */
/**
 * Abstraction for terser and simpler GA event for errors (also automatically
 * logs to console.)
 *
 * @param {string} context Context of error describing the function or action
 *   where this error may take place, e.g. "Add To Cart", "Update Cart Count",
 *   "IPinfo", etc.
 * @param {string} message
 */
window.APP.gaError = function(context, message) {
  setTimeout(() => {
    ga('send', {
      hitType: 'event',
      eventCategory: 'Errors',
      eventAction: context,
      eventLabel: message,
    });
    console.error(message);
  }, 1000);
}

/**
 * @param {string} eventName
 * @param {object} [eventParams = {}]
 */
window.APP.gtagEvent = function(eventName, eventParams = {}) {
  gtag('event', eventName, { send_to: 'G-3MCCQEMV6N', ...eventParams });
};

/**
  * @param {function} callback
  * @param {number} delay milliseconds
  * @returns {function}
  */
window.APP.debounce = function(callback, delay = 200) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => callback.apply(this, args), delay);
  };
};

/**
 * TODO: Possibly ensure click (or touch) does not trigger any events below.
 *
 * @param {function} doOnClick
 * @returns {number} timeoutID
 */
window.APP.doOnClickAnywhere = (function(doOnClick) {
  function start() {
    window.addEventListener('click', hide);
  }

  function hide() {
    doOnClick();
    window.removeEventListener('click', hide);
  }

  return function() {
    setTimeout(start, 0);
  };
});

/**
 * Transform Shopify CDN image asset to specified dimension and/or crop.
 *
 * @param CDNUrl {string} URL to Shopify CDN asset
 * @param options {Object}
 * @param options.img_url {string} E.g "10x10", "10x", "master"
 * @param options.crop {string} "top", "center", "bottom", "left", or "right"
 * @returns {string}
 *
 * @see https://shopify.dev/docs/api/liquid/filters/img_url
 */
window.APP.imgURL = function(CDNUrl, options) {
  let params = '';

  if ('img_url' in options) {
    if (options.img_url != 'master') {
      params += ('img_url' in options) ? `_${options.img_url}` : '';
    }
  }

  if ('crop' in options) {
    switch(options.crop) {
      case 'top':
      case 'center':
      case 'bottom':
      case 'left':
      case 'right':
        params += `_crop_${options.crop}`;
    }
  }

  const url = new URL(CDNUrl);
  const pathArray = url.pathname.split('/');
  const imgFrag = pathArray.pop().split('.').slice(0,-1).join('.');
  const pathBase = `//${url.hostname}${pathArray.join('/')}/${imgFrag}`;
  const fileExt = url.pathname.split('.').pop();
  return `${pathBase}${params}.${fileExt}`;
}

/**
 *
 * @param {string} url
 * @param {string} title
 * @param {string} featuredImageUrl
 * @param {string} sizes
 * @param {string} srcsetWidths
 * @param {object} [opts={}]
 * @returns {string} HTML
 */
window.APP.productGridItem = function(url, title, featuredImageUrl, sizes, srcsetWidths, opts = {}) {
  const srcset = srcsetWidths.split(',').map(width => {
      const src = window.APP.imgURL(featuredImageUrl, { img_url: `${width}x` });
      return `${src} ${width}w`;
  }).join(',');

  return `
    <li class="product-grid-item product-grid-item__no_visited ${opts.class ?? ''}">
      <a href="${url}" class="product-grid-image-link">
        <img class="product-grid-image product-media"
          src="${featuredImageUrl}"
          width="300"
          height="200"
          sizes="${sizes}"
          srcset="${srcset}"
          alt=""
          loading="lazy">
      </a>
      <p class="product-grid-title"><a class="product-grid-title-link" href="${url}">${title}</a></p>
    </li>
  `;
}

/**
 * Make GraphQL query to the Shopify Storefront API.
 *
 * @param {{query: string, variables?: object}} graphQlRequest
 * @returns {Promise<JSON>} Technically an object or array.
 */
window.APP.queryStorefront = async function(graphQlRequest) {
  const response = await fetch('https://p4-lmd.myshopify.com/api/2025-01/graphql.json', {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': 'e5ddb46279ee02d8374146efbeb2d6f0',
    },
    body: JSON.stringify(graphQlRequest),
  });
  if (!response.ok) throw `Bad Storefront API response: ${response.status}`;

  const payload = await response.json();
  if (payload.errors) throw `Bad Storefront API query: ${JSON.stringify(payload.errors)}`;

  const data = payload.data;
  return data;
}

/**
 * @param {Element} target
 * @param {string} eventName
 * @param {object} eventParams
 */
window.APP.trackClick = function(target, eventName, eventParams = {}) {
  target.addEventListener('click', () => window.APP.gtagEvent(eventName, eventParams));
};

/**
 * @param {Element} target
 * @param {string} eventName
 * @param {object} [eventParams = {}]
 * @param {number} [threshold = .5]
 */
window.APP.trackVisibilityOnce = function(target, eventName, eventParams = {}, threshold = .5) {
  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        window.APP.gtagEvent(eventName, eventParams)
        obs.unobserve(entry.target); // Stop observing so the event fires only once.
      }
    });
  }, { threshold });
  observer.observe(target);
};


// Currency Converter ==========================================================
window.APP.convertPrice = (function() {
  const LOCAL_STORAGE_NAME = 'p4_currency_code';
  /**
   * Country code in ISO ALPHA-2 Code
   * Currency code in ISO 4217 country code
   *
   * US  USD  U.S. dollar                            $100
   * EU  EUR  Euro                                   €100
   * GB  GBP  Pound sterling                         £100
   * HK  HKD  Hong Kong dollar                       HK$100
   * AU  AUD  Australian dollar                      AUD $100
   * CH  CHF  Swiss franc                            CHF 100
   * JP  JPY  Japanese yen                           ¥100
   * CA  CAD  Canadian dollar                        CAD $100
   * SG  SGD  Singapore dollar                       S$100
   * CN  CNY  Chinese yuan or renminbi               ¥100
   * RU  RUB  Russian ruble                          100₽
   * TW  TWD  Taiwan New Dollar                      NT$100
   * KR  KRW  South Korean (or Korean Republic) won  ₩100
   * AE  AED  United Arab Emirates dirham            100AED
   */
  const CURRENCIES = {
    EU: { countryCode: 'EU', curCode: 'EUR', sym: '€',   showCurCode: false, isPrefixed: true, },
    US: { countryCode: 'US', curCode: 'USD', sym: '$',   showCurCode: false, isPrefixed: true, },
    GB: { countryCode: 'GB', curCode: 'GBP', sym: '£',   showCurCode: false, isPrefixed: true, },
    CN: { countryCode: 'CN', curCode: 'CNY', sym: '¥',   showCurCode: false, isPrefixed: true, },
    TH: { countryCode: 'TH', curCode: 'THB', sym: '฿',   showCurCode: true,  isPrefixed: true, },
    JP: { countryCode: 'JP', curCode: 'JPY', sym: '¥',   showCurCode: false, isPrefixed: true, },
    AU: { countryCode: 'AU', curCode: 'AUD', sym: '$',   showCurCode: true,  isPrefixed: true, },
    CA: { countryCode: 'CA', curCode: 'CAD', sym: '$',   showCurCode: true,  isPrefixed: true, },
    CH: { countryCode: 'CH', curCode: 'CHF', sym: '',    showCurCode: true,  isPrefixed: true, },
    SG: { countryCode: 'SG', curCode: 'SGD', sym: 'S$',  showCurCode: false, isPrefixed: true, },
    TW: { countryCode: 'TW', curCode: 'TWD', sym: 'NT$', showCurCode: false, isPrefixed: true, },
    HK: { countryCode: 'HK', curCode: 'HKD', sym: 'HK$', showCurCode: false, isPrefixed: true, },
    KR: { countryCode: 'KR', curCode: 'KRW', sym: '₩',   showCurCode: false, isPrefixed: true, },
    AE: { countryCode: 'AE', curCode: 'AED', sym: 'AED', showCurCode: false, isPrefixed: false, },
    RU: { countryCode: 'RU', curCode: 'RUB', sym: '₽',   showCurCode: false, isPrefixed: false, },
  };
  const DEFAULT_COUNTRY_CODE = 'EU';
  let COUNTRY_CODE = DEFAULT_COUNTRY_CODE;
  let CURRENCY_DATA = CURRENCIES[COUNTRY_CODE];
  const container = document.querySelector('#js-change-currency');

  const setConfig = function(countryCode) {
    COUNTRY_CODE = (countryCode in CURRENCIES) ? countryCode : DEFAULT_COUNTRY_CODE;
    CURRENCY_DATA = CURRENCIES[COUNTRY_CODE];
    localStorage.setItem(LOCAL_STORAGE_NAME, COUNTRY_CODE);
  }

  const convertPrice = function(eurPrice, curData = CURRENCY_DATA) {
    const code = curData.showCurCode ? `<span class="currency-code">${curData.curCode}</span> ` : '';
    const price = Currency.convert(eurPrice / 100, 'EUR', curData.curCode).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `${code}${curData.isPrefixed ? `${curData.sym}${price}` : `${price}${curData.sym}`}`;
  };

  const convertAllPrices = function() {
    document.querySelectorAll('.js-money').forEach(price => {
      price.innerHTML = convertPrice(price.dataset.price);
    });
  };

  const currencyList = (function() {
    const list = document.querySelector('#js-change-currency__list');
    const defaultItemClassList = ['change-currency__item', 'js-change-currency__item'];
    const activeItemClassList = ['change-currency__item--active', 'js-change-currency__item--active'];

    // List items cannot be selected until elements are generated by
    // `setList()` below.
    let listItems;

    const setList = (function() {
      const productPriceEl = document.getElementById('js-ProductPrice');
      const inactiveItemClass = defaultItemClassList.join(' ');
      const activeItemClass = [...defaultItemClassList, ...activeItemClassList].join(' ');

      return function() {
        let html = '';

        for (const cur in CURRENCIES) {
          const itemClasses = (CURRENCIES[cur].countryCode == COUNTRY_CODE) ? activeItemClass : inactiveItemClass;
          html += `<li class="${itemClasses}" data-country-code="${CURRENCIES[cur].countryCode}"><span class="change-currency__country">${CURRENCIES[cur].curCode}</span><span>${convertPrice(productPriceEl.dataset.price, CURRENCIES[cur])}</span></li>`;
        }

        list.innerHTML = html
        listItems = list.querySelectorAll('.js-change-currency__item');
      };
    })();

    const initList = function() {
      // Not a product page, exit early
      if (!container) return;

      setList();

      // Javascript is necessary in order to dismiss the list-menu on selection.
      // This is not possible with CSS.
      container.addEventListener('mouseenter', () => list.hidden = false);
      container.addEventListener('mouseleave', () => list.hidden = true);

      const hideList = function() {
        list.hidden = true;
        window.removeEventListener('touchend', hideList);
      };

      document.querySelector('#js-change-currency__label').addEventListener('click', () => {
        let list_is_visible = (list.clientHeight !== 0);
        if (list_is_visible) {
          list.hidden = true;
        } else {
          list.hidden = false;
          setTimeout(function() {
            window.addEventListener('touchend', hideList);
          }, 0);
        }
      });

      // Resolve race condition w/r/t `setList()` above; let DOM update then
      // attach events.
      setTimeout(() => {
        listItems.forEach(li => {
          // Non-arrow function binds `this` to `li`
          li.addEventListener('click', function() {
            setConfig(this.dataset.countryCode);
            convertAllPrices();
            list.querySelector('.js-change-currency__item--active').classList.remove(...activeItemClassList);
            this.classList.add(...activeItemClassList);
            list.hidden = true;
          })
        });
      }, 0);
    };

    const hideContainer = function() {
      if (container) container.style.display = 'none';
    };

    return {
      initList,
      setList,
      hideContainer,
    };
  })();

  (function init() {
    const countryCode = localStorage.getItem(LOCAL_STORAGE_NAME);

    const initAction = function(countryCode) {
      setConfig(countryCode);
      if (COUNTRY_CODE !== DEFAULT_COUNTRY_CODE) {
        document.body.classList.add('alt-currency');
        convertAllPrices();
      }
      currencyList.initList();
    }

    if (countryCode) {
      initAction(countryCode);
    } else {
      fetch('https://ipinfo.io?token=a22bc8bbd6d5d4')
        .then(function(res) {
          if (!res.ok) {
            const msg = (res.status === 429) ?
              'Currency conversion error: Exceeded monthly request limit for IP geolocation.' :
              `HTTP response status code: "${res.status}".`;
            window.APP.gaError('IPinfo', msg);
            return { country: DEFAULT_COUNTRY_CODE };
          }

          return res.json();
        })
        .then(function(json) {
          initAction(json.country);
        })
        .catch(function(err) {
          currencyList.hideContainer();
          window.APP.gaError('IPinfo', err);
        });
    }
  })();

  return {
    convert: convertPrice,
    refreshList: currencyList.setList,
  };
})();


// Stage Light =================================================================
/**
 * @param {object} el - The stage light element
 * @returns {object} The (modified) stage light element
 *
 * NOTE: The overflow property on the <body> disables scrolling outside the
 *       modal popup. However, it can be inconsistent on iOS devices.
 *
 *       If it persists, this may be an option:
 *         - <https://stackoverflow.com/a/57773610>
 *
 *       The following failed to work:
 *         - <https://github.com/willmcpo/body-scroll-lock> resets to the top
 *           on iOS.
 *         - <https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open>
 *           prevents scrolling within the modal popup.
 */
window.APP.attachStageLight = function(el) {
  el.dim = function() {
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    // Zero duration timeout allows CSS transition to take place
    setTimeout(() => this.classList.add('is-dimmed'), 0);
  }

  el.restore = function(transition = 0) {
    this.classList.remove('is-dimmed');
    document.body.style.overflow = '';
    // Hide after CSS transition
    setTimeout(() => this.hidden = true, transition);
  }

  return el;
}


// Drawer ======================================================================
// Drawer constructor ----------------------------------------------------------
window.APP.Drawer = class {
  /**
  * @param {string} drawerID
  * @param {string} openBtnID
  * @param {string} closeBtnClass
  * @param {object} [options]
  * @param {string} [options.trackOpen]
  * @param {string} [options.trackClose]
  */
  constructor(drawerID, openBtnID, closeBtnClass, options = {}) {
    this.drawer = document.querySelector(`#${drawerID}`);
    this.stageLight = window.APP.attachStageLight(document.querySelector('#js-stage-light-drawer'));
    this.drawerTransitionDur = window.APP.config.drawerTransitionDur;
    this.navIsOpen = false; // Closed at load
    this.trackOpen = options.trackOpen;
    this.trackClose = options.trackClose;

    document.querySelector(`#${openBtnID}`).addEventListener('click', () => this.toggle());
    document.querySelectorAll(`.${closeBtnClass}`).forEach(el => el.addEventListener('click', () => this.hide()));
  }

  hide() {
    this.navIsOpen = false;
    this.drawer.classList.remove('is-open');
    this.stageLight.restore(this.drawerTransitionDur);

    if (this.trackClose) window.APP.gtagEvent(this.trackClose);
  }

  show() {
    this.navIsOpen = true;
    this.stageLight.dim();

    // Zero duration timeout allows CSS transition to take place
    setTimeout(() => this.drawer.classList.add('is-open'), 0);

    if (this.trackOpen) window.APP.gtagEvent(this.trackOpen);
  }

  toggle() {
    if (this.navIsOpen) this.hide();
    else this.show();
  }
};

// Instantiate drawers ---------------------------------------------------------
window.APP.drawers = {};
window.APP.drawers.nav = new window.APP.Drawer('js-menubar', 'js-menubar-button', 'js-close-drawer', {
  trackOpen: 'menubar_mobile_open',
  trackClose: 'menubar_mobile_close'
});


// Search ======================================================================
/**
 * Desktop Search Bar
 *
 * Click on the search-link and the form closes only when the link is re-clicked
 * again. However, a click on the form via a hover and it goes away after a
 * click on anything. The search-link click behavior is maintained even after a
 * subsequent form click. Hover-actions are controlled via CSS.
 */
(function() {
  const link = document.getElementById('js-menubar--link__search-bar');
  const wrapper = document.getElementById('js-menubar-search-bar--wrapper');
  const input = wrapper.getElementsByClassName('c-js-search-bar--input')[0];

  const hideFormOnClickAnywhere = window.APP.doOnClickAnywhere(() => {
    link.classList.remove('js-menubar--link__search-bar__active');
    wrapper.classList.remove('js-menubar-search-bar--wrapper__visible');
  });

  link.addEventListener('click', (event) => {
    event.preventDefault();

    link.classList.toggle('js-menubar--link__search-bar__active');
    wrapper.classList.toggle('js-menubar-search-bar--wrapper__visible');

    // Focus input field and add window-wide click-event listener only when form
    // has been toggled on.
    if (wrapper.classList.contains('js-menubar-search-bar--wrapper__visible')) {
      input.focus();
      hideFormOnClickAnywhere();
    }
  });

  wrapper.addEventListener('click', (event) => {
    link.classList.add('js-menubar--link__search-bar__active');
    wrapper.classList.add('js-menubar-search-bar--wrapper__visible');
    hideFormOnClickAnywhere();

    // Stop event propagation
    if (wrapper.classList.contains('js-menubar-search-bar--wrapper__visible'))
      return event.stopPropagation();
  })
})();


/**
 * Add predictive search.
 */
(function() {
  // Customized Orama Highlight for search terms <https://github.com/oramasearch/highlight>
  const highlight = new ((function CustomOramaHighlight(){const s={caseSensitive:!1,wholeWords:!1,HTMLTag:"b"};return class{options;_positions=[];_HTML="";_searchTerm="";_originalText="";constructor(t=s){this.options={...s,...t}}highlight(t,i){this._searchTerm=i??"",this._originalText=t??"";let o=this.options.caseSensitive??s.caseSensitive,e=this.options.wholeWords??s.wholeWords,n=this.options.HTMLTag??s.HTMLTag,d=o?"g":"gi",p=e?"\\b":"",m=this.escapeRegExp(o?this._searchTerm:this._searchTerm.toLowerCase()).trim().split(/\s+/).join("|"),g=new RegExp(`${p}${m}${p}`,d),T=[],r=[],a,l=0,u=-1;for(;(a=g.exec(this._originalText))!==null&&g.lastIndex!==u;){u=g.lastIndex;let c=a.index,_=c+a[0].length-1;T.push({start:c,end:_}),r.push(this._originalText.slice(l,c)),r.push(`<${n}>${a[0]}</${n}>`),l=_+1}return r.push(this._originalText.slice(l)),this._positions=T,this._HTML=r.join(""),this}trim(t,i=!0){if(this._positions.length===0)return`${this._HTML.substring(0,t)}${i?"...":""}`;if(this._originalText.length<=t)return this._HTML;let o=this._positions[0].start,e=Math.max(o-Math.floor(t/2),0),n=Math.min(e+t,this._originalText.length),h=`${e===0||!i?"":"..."}${this._originalText.slice(e,n)}${n<this._originalText.length&&i?"...":""}`;return this.highlight(h,this._searchTerm),this._HTML}get positions(){return this._positions}get HTML(){return this._HTML}escapeRegExp(t){return t.replace(/[.*+?^${}()|[\]\\]/g,"\\$&")}};})());

  const template = document.querySelector('#predictive-search-template');
  const searchBarInputs = document.querySelectorAll('.c-js-search-bar--input');
  searchBarInputs.forEach(input => input.addEventListener('input', (() => {
    const searchBarWrapper = input.parentElement.parentElement;
    const formSubmitButton = input.parentElement.querySelector('.c-search-bar--submit-btn');
    const predictiveSearchComponent = input.parentElement.parentElement.querySelector('.js-predictive-search');
    const predictiveSearchList = predictiveSearchComponent.querySelector('.js-predictive-search--list');
    const predictiveSubmitButton = predictiveSearchComponent.querySelector('.js-predictive-search--submit');
    let navigableElements = makeNavigableElements(predictiveSearchList);

    // For arrow-key navigation
    function makeNavigableElements(list) {
      return [input, ...list.querySelectorAll('a'), predictiveSubmitButton];
    }

    // Make "View All" echo the search bar submit button.
    /**
     * Due to a longstanding macOS Safari bug related to `:focus-within`, the
     * dropdown gets incorrectly dismissed on `click` of the "View All" button.
     * The switch to `mousedown` is a workaround, which is not ideal UX but most
     * users should not notice the difference.
     *
     * See
     * - Changelog v1.37.1 (2025-3-7)
     * - Trello: <https://trello.com/c/IHvXGGuS>
     *
     * If this bug is ever resolved, revert event type to `click`.
     */
    predictiveSubmitButton.addEventListener('mousedown', (event) => {
      formSubmitButton.click();
    });

    // Add arrow-key navigation between the search field, predictive search list,
    // and the "View All" button. Wrap around if necessary.
    let currentIndex = 0;
    function moveFocus(newIndex) {
      // Ensure the new index is within bounds and wrap-around if necessary.
      if (newIndex < 0) {
        newIndex = navigableElements.length - 1;
      } else if (newIndex >= navigableElements.length) {
        newIndex = 0;
      }
      currentIndex = newIndex;
      navigableElements[currentIndex].focus();
    }
    searchBarWrapper.addEventListener('keydown', (event) => {
      switch(event.key) {
        case 'ArrowUp':
          event.preventDefault();
          moveFocus(currentIndex - 1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          moveFocus(currentIndex + 1);
          break;
      }
    });
    searchBarWrapper.addEventListener('focusin', (event) => {
      const newIndex = navigableElements.indexOf(event.target);
      if (newIndex !== -1) {
        currentIndex = newIndex;
      }
    });

    predictiveSearchList.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        document.activeElement.blur();
      }
    });

    // Prevent dismissal of the predictive search list on touch devices (such as
    // when the onscreen keyboard is dismissed.)
    input.addEventListener('blur', (event) => {
      if (window.isTouchDevice() && document.activeElement === document.body) {
        formSubmitButton.focus();
      }
    });

    function makeListItem(isFirst, product, searchTerm) {
      const templateClone = template.content.cloneNode(true);
      const anchor = templateClone.querySelector('a');
      anchor.href = product.url;
      if (isFirst) anchor.tabIndex = 0;
      templateClone.querySelector('img').src = window.APP.imgURL(product.featured_image.url, { img_url: '180x' });
      templateClone.querySelector('.js-predictive-search--title').innerHTML = highlight.highlight(product.title, searchTerm).HTML;
      const priceEl = templateClone.querySelector('.js-predictive-search--price');
      priceEl.dataset.price = product.price_min * 100;
      const priceMax = Math.max(product.price_max, product.compare_at_price_max);
      if (priceMax >= window.APP.config.priceOnRequest.min) {
        priceEl.textContent = window.APP.config.priceOnRequest.note;
        priceEl.classList.add('predictive-search--price__request');
        priceEl.classList.remove('js-money');
      } else {
        priceEl.textContent = window.APP.convertPrice.convert(product.price_min * 100);
        priceEl.classList.add('js-money');
        priceEl.classList.remove('predictive-search--price__request');
      }
      return templateClone;
    }

    return async (event) => {
      const searchTerm = event.target.value.trim();
      if (searchTerm === '') return predictiveSearchList.innerHTML = '';
      let response;
      try {
        response = await fetch(window.Shopify.routes.root + 'search/suggest.json?resources[type]=product&resources[limit]=9&q=' + searchTerm);
      } catch (error) {
        console.error(error);
      }
      if (!response.ok) return;
      let isFirst = true;
      const listItems = document.createDocumentFragment();
      const products = (await response.json()).resources.results.products;
      products.forEach((product) => {
        listItems.appendChild(makeListItem(isFirst, product, searchTerm));
        isFirst = false;
      });
      predictiveSearchList.replaceChildren(listItems);
      navigableElements = makeNavigableElements(predictiveSearchList);
      formSubmitButton.tabIndex = -1;
    };
  })()));

  searchBarInputs.forEach(input => {
    if (input.value.trim() !== '') {
      input.dispatchEvent(new Event('input'));
    }
  });
})();


/**
 * Update all cart count figures ===============================================
 *
 * @param {number} [tempIncrement] Optionally, immediately and temporarily
 *   update the cart count while an AJAX call retrieves the real figure.
 */
window.APP.updateCartCount = (function() {
  const cartCount = document.querySelectorAll('.js-cart-count');

  const update = function(count) {
    for (const cc of cartCount) {
      cc.textContent = cc.dataset.count = count;
      cc.hidden = false;
    }
  }

  return function(tempIncrement) {
    if (Number.isInteger(tempIncrement)) {
      update(Number(cartCount[0].dataset.count) + tempIncrement);
    }

    fetch('/cart.js')
      .then((res) => {
        if (!res.ok) throw `HTTP response status code: ${res.status}`;
        return res.json();
      })
      .then((json) => {
        update(json.item_count);
      })
      .catch((err) => {
        window.APP.gaError('Update Cart Count', err);
      });
  };
})();


/**
 * Header ======================================================================
 */
// News Ticker -----------------------------------------------------------------
(function newsTicker() {
  const slides = document.querySelectorAll('.js-news-ticker--slide');
  let splide;

  if (slides.length > 1) {
    // Unhide non-first slides
    slides.forEach(slide => slide.hidden = false);

    splide = new Splide('.news-ticker', {
      type: 'loop',
      arrows: false,
      autoplay: true,
      interval: 4500,
      fixedWidth: '100vw',
      pagination: false,
    }).mount();
  }

  if (splide) {
    document.querySelectorAll('.news-ticker--details').forEach(detail => {
      detail.addEventListener('toggle', () => {
        if (detail.open) {
          splide.Components.Autoplay.pause();
        } else {
          splide.Components.Autoplay.play();
        }
      });
    });
  }
})();

// Menubar ---------------------------------------------------------------------
(function menubar() {
  const headerNav = document.querySelector('#js-header-nav');
  const isDesktop = (function() {
    const headerSmallPx = parseInt(getComputedStyle(document.body).getPropertyValue('--js-breakpointHeaderSmall'));
    return () => window.innerWidth > headerSmallPx;
  })();

  /**
   * Progressively enhance big menu dropdowns for desktops.
   *
   * - Programmatically position each dropdown. This introduces to layout jank
   *   at load-time but ¯\_(ツ)_/¯.
   */
  (function desktopMenubar() {
    const dropdownMenuContainers = document.querySelectorAll('.menubar--menu[data-active-child]');

    function setDropdownMenuPositions() {
      if (!isDesktop()) return;
      document.documentElement.style.setProperty(
        '--dropdown-width',
        `${document.documentElement.clientWidth}px`
      );
      dropdownMenuContainers.forEach(el => {
        document.documentElement.style.setProperty(
          `--dropdown-left__${el.dataset.type}`,
          `${el.getBoundingClientRect().left * -1}px`
        );
      })
    }

    setDropdownMenuPositions();
    const handleWindowResize = window.APP.debounce(setDropdownMenuPositions);
    window.addEventListener('resize', handleWindowResize);
  })();

  /**
   * Add interactivity to mobile drawer submenu navigation.
   *
   * NOTE: Drawer control is managed by `window.APP.Drawer()` above.
   */
  (function mobileMenubar() {
    const isAllJewelry = (evt) => evt.target.classList.contains('js-is-all-jewelry');
    const transitionDur = window.APP.config.drawerTransitionDur;
    const zones = headerNav.querySelectorAll('.js-menubar--zone');
    const categorySubmenus = headerNav.querySelectorAll('.menubar--submenu__products');
    const jewelrySubcategorySubmenus = headerNav.querySelectorAll('.js-menubar--subcategories__jewelry');

    headerNav.querySelectorAll('.js-menubar--shift-in').forEach(el => {
      el.addEventListener('click', handleShiftIn);
    });
    headerNav.querySelectorAll('.js-menubar--shift-back').forEach(el => {
      el.addEventListener('click', handleShiftBack);
    })
    track();

    function handleShiftIn(evt) {
      if (isDesktop()) return;
      if (isAllJewelry(evt)) {
        window.location = evt.target.parentElement.parentElement.parentElement.parentElement.querySelector('.menubar--link__top').href;
        return;
      }
      evt.preventDefault();
      evt.target.nextElementSibling.style.display = 'block';
      zones.forEach(el => el.dataset.shift = parseInt(el.dataset.shift) + 1);
    }

    function handleShiftBack(evt) {
      if (isDesktop()) return;
      evt.preventDefault();
      zones.forEach(el => el.dataset.shift = parseInt(el.dataset.shift) - 1);
      if (zones[0].dataset.shift === '0') {
        categorySubmenus.forEach(el => setTimeout(() => el.style.display = '', transitionDur));
      } else {
        jewelrySubcategorySubmenus.forEach(el => setTimeout(() => el.style.display = '', transitionDur));
      }
    }

    function track() {
      if (isDesktop()) return;
      // if (typeof gtag === 'undefined') return;

      const allJewelryEl = headerNav.querySelector('.js-is-all-jewelry');
      window.APP.trackClick(allJewelryEl, 'menubar_mobile_click_collection', { target_name: allJewelryEl.textContent });

      headerNav.querySelectorAll('.menubar--link__subcategory').forEach(link => {
        window.APP.trackClick(link, 'menubar_mobile_click_collection', { target_name: link.textContent });
      });

      headerNav.querySelectorAll('.menubar--link__info').forEach(link => {
        window.APP.trackClick(link, 'menubar_mobile_click_info', { target_name: link.textContent });
      });
    }
  })();
})();


/**
 * User Interaction ============================================================
 */
// Email Capture ---------------------------------------------------------------
(function() {
  /**
   * Show (static) email capture dialog after the user scrolls down a pre-
   * determined amount, i.e. after a minimal amount of user interaction. The
   * scroll amount is determined via CSS using the `top` property. If there is
   * insufficient content for scrolling, immediately show the dialog.
   *
   * FIXME: Does not appear correctly if loaded window's scroll position is
   *        below the form.
   * FIXME: Does not support window resizing?
   * TODO: Switch to the <dialog> element.
   */
  // Exit. The subscriber form does not exist.
  const subscriber = document.getElementById('js-email-capture');
  if (!subscriber) return;

  // Hide the form and exit. The user has already subscribed or has previously
  // dismissed the form.
  if (localStorage.getItem('p4_dismissed_email_subscriber')) {
    subscriber.hidden = true;
    return;
  }

  // If the viewport is too short, immediately show the subscriber form. Other-
  // wise, hide until the user has scrolled down beyond a threshold.
  const isTallEnough = function() {
    const htmlHeight = document.documentElement.getBoundingClientRect().height;
    const subscriberHeight = subscriber.getBoundingClientRect().y;
    return htmlHeight > subscriberHeight;
  }
  if (!isTallEnough()) {
    subscriber.classList.add('js-email-capture__fixed');
  } else {
    (new IntersectionObserver(entries => {
      if (entries[0].boundingClientRect.y < window.innerHeight) {
        subscriber.classList.add('js-email-capture__fixed');
      }
    }, { threshold: 1.0 })).observe(subscriber);
  }

  // Permanently hide dialog on successful submission of any email subscription
  // form.
  document.querySelectorAll('.js-c-email-subscription-form').forEach(form => {
    form.addEventListener('submit', evt => {
      localStorage.setItem('p4_dismissed_email_subscriber', 1);
      subscriber.hidden = true;
    });
  })

  // Permanently hide dialog on close button click.
  document.getElementById('js-email-capture--close-button').addEventListener('click', () => {
    localStorage.setItem('p4_dismissed_email_subscriber', 1);
    subscriber.hidden = true;
  });
})();

// Login Popup -----------------------------------------------------------------
(function() {
  const popup = document.querySelector('#js-login-popup');
  if (!popup) return;

  window.APP.loginPopup = {
    open: ({ showNotice } = {}) => {
      popup.dataset.show = 'login';
      if (showNotice === 'add' || showNotice === 'view') {
        popup.querySelector('#js-alert__login-popup').dataset.show = showNotice;
      } else {
        popup.querySelector('#js-alert__login-popup').dataset.show = '';
      }
      popup.showModal();
    },
  };

  if (window.location.hash === '#reset-success') {
    window.APP.loginPopup.open();
  }

  // TODO: Also watch for `/account`, others?
  const loginPath = '/account/login';

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (link && link.href.includes(loginPath)) {
      event.preventDefault();
      window.APP.loginPopup.open();
    }
  });

  popup.querySelector('#js-login-popup--close-button').addEventListener('click', () => {
    popup.close();
  });

  const loginEmailInput = document.getElementById('js-login-email');
  const resetEmailInput = document.getElementById('js-password-reset-email');
  popup.querySelector('#js-login-popup--show-reset').addEventListener('click', () => {
    resetEmailInput.value = loginEmailInput.value;
    resetEmailInput.focus();
    popup.dataset.show = 'reset';
  });
  popup.querySelector('#js-login-popup--show-login').addEventListener('click', () => {
    loginEmailInput.value = resetEmailInput.value;
    loginEmailInput.focus();
    popup.dataset.show = 'login';
  });
})();

/**
 * Wishlist --------------------------------------------------------------------
 */
window.APP.wishlist = (function() {
  const SERVER_URL = 'https://p4-wishlist.fly.dev/api/';
  const LOCAL_STORAGE_NAME = 'p4_wishlist';

  // Exit early if wishlist token or data is not available.
  const wishlistTokenElement = document.querySelector('#wishlist-token');
  const wishlistJsonElement = document.querySelector('#wishlist-json');
  if (!wishlistTokenElement || !wishlistJsonElement) {
    return { isEnabled: false };
  }
  const wishlistToken = JSON.parse(wishlistTokenElement.textContent).token;
  const wishlistJson = wishlistJsonElement.textContent.trim();

  let data = [];
  let callbacks = [];

  setData(JSON.parse(wishlistJson));
  window.addEventListener('storage', (event) => {
    if (event.key === LOCAL_STORAGE_NAME) {
      setData(JSON.parse(event.newValue) || []);
    }
  });

  /**
   * Only update wishlist data here.
   */
  function setData(newData) {
    data = newData;
    localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(newData));
    callbacks.forEach(callback => callback());
  }

  /**
   * Add or delete a wishlist item via API.
   *
   * @param {'POST' | 'DELETE'} method
   * @param {string} productId
   * @param {string} variantId
   * @returns {Promise<Array>}
   */
  async function action(method, productId, variantId) {
    let wishlist;
    let response;
    try {
      response = await fetch(
        SERVER_URL,
        {
          method,
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Shop-Domain': 'p4-lmd.myshopify.com',
            'Authorization': `Bearer ${wishlistToken}`,
          },
          body: JSON.stringify({
            productId: Number(productId),
            // variantId,
          }),
        }
      );
    } catch(error) {
      console.error('Wishlist Request Failed: ', error); // CORS failure?
    }

    if (!response || !response.ok) {
      const errorMessage = await response.json();
      console.error('Wishlist Error: ', errorMessage);
      return null;
    }

    window.APP.gtagEvent(
      method === 'POST' ? 'wishlist_click_add' : 'wishlist_click_remove',
      { page_type: document.body.dataset.template },
    );
    ({ data: wishlist } = await response.json()); // Single-line destructure
    setData(wishlist);
    return wishlist;
  }

  /**
   * @param {Object} [options={}]
   * @property {number} [options.limit] Limit the number of products returned.
   * @returns {Promise<Array<{
   *   id: string,
   *   link: string,
   *   img: string,
   *   isOneOfAKind: boolean,
   *   title: string,
   *   price: number,
   *   compareAtPrice: number,
   *   hasOnlyDefaultVariant: boolean,
   *   variants: Array<{ id: string, title: string }>
   * }>>}
   */
  async function makeProductList(options = {}) {
    if (!data.length) return [];

    let productIds = data.map(item => `gid://shopify/Product/${item.productId}`);
    if (Number.isInteger(options.limit)) {
      productIds = productIds.slice(0, options.limit);
    }

    const { products: productsData } = await window.APP.queryStorefront({
      query: /* graphql */`
        query WishlistProducts($ids: [ID!]!) {
          products: nodes(ids: $ids) {
            ... on Product {
              id
              onlineStoreUrl
              featuredImage {
                url
              }
              tags
              title
              priceRange {
                minVariantPrice {
                  amount
                }
              }
              compareAtPriceRange {
                minVariantPrice {
                  amount
                }
              }
              variants(first: 15) {
                nodes {
                  id
                  title
                }
              }
            }
          }
        }
      `,
      variables: {
        ids: productIds
      }
    });

    // Format wishlist product data for list.
    return productsData
      .filter(product => product !== null)
      .filter(product => product.onlineStoreUrl !== null)
      .map(product => {
        return {
          id: product.id.replace('gid://shopify/Product/', ''),
          link: product.onlineStoreUrl,
          img: product.featuredImage?.url ?? null,
          isOneOfAKind: product.tags.includes('One-of-a-Kind'),
          title: product.title,
          price: product.priceRange.minVariantPrice.amount * 100,
          compareAtPrice: product.compareAtPriceRange.minVariantPrice.amount * 100,
          hasOnlyDefaultVariant: product.variants.nodes.length === 1,
          variants: product.variants.nodes.map(variant => ({
            id: variant.id.replace('gid://shopify/ProductVariant/', ''),
            title: variant.title,
          })),
        };
      });
  }

  return {
    isEnabled: true,
    add: (productId, variantId) => action('POST', productId, variantId),
    remove: (productId, variantId) => action('DELETE', productId, variantId),
    get data() { return [...data]; },
    makeProductList,
    registerUpdateListener(callback) { callbacks.push(callback); },
  };
})();


/**
 * Contact Form Helper =========================================================
 */
/**
 * @param {string} formId
 * @param {string} inputName
 * @param {string} inputValue
 */
window.APP.addHiddenFormField = function(formId, inputName, inputValue) {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = `contact[${inputName}]`;
  input.value = inputValue;
  document.getElementById(formId)?.prepend(input);
};

/**
 * Add a hidden form field w/ Google Analytics client ID.
 * @param {string} formId
 */
window.APP.addGAFormField = function(formId) {
  const add = () => {
    ga(tracker => {
      const clientId = tracker.get('clientId');
      if (clientId === null) return;
      window.APP.addHiddenFormField(formId, 'googleAnalyticsClientId', clientId);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', add);
  } else {
    add();
  }
};

/**
 * Forms are submitted via POST. However, in case of a form submission error,
 * the user is returned to the same page with query parameters in the URL. This
 * removes hidden fields from the query parameters. NOTE: `history.replaceState`
 * cannot prevent the creation of a new entry in the global browser history.
 */
window.APP.removeQueryParams = function() {
  const queryString = window.location.search;
  if (queryString === '') return;

  const params = new URLSearchParams(queryString);
  params.delete('contact[googleAnalyticsClientId]');
  params.delete('contact[recentViews]');
  params.delete('contact[shopifyCustomerId]');
  history.replaceState(null, '', (window.location.pathname + '?' + params.toString()));
};
